// FAQ — dedicated page. Pulled out of the homepage so the conversion funnel
// stays focused (Hero → Categories → Selection → Assessment).
// Schema: FAQPage JSON-LD for SEO.
import { useState } from "react";
import { ChevronDown, MessageCircle, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import Nav from "@/components/landing/Nav";
import Footer from "@/components/landing/Footer";
import SEO from "@/components/shared/SEO";

const RED = "#E11D2A";

const FAQS = [
  {
    q: "How does BestRx work?",
    a: "Complete a short medical assessment, get reviewed by a licensed provider, and have medication shipped discreetly to your door. The entire process is online — no in-person visits, no insurance hassle.",
  },
  {
    q: "Is this real medication?",
    a: "Yes. Every medication is compounded by a US-licensed pharmacy and dispensed only after a licensed provider reviews your assessment and approves your treatment plan.",
  },
  {
    q: "Do I need insurance?",
    a: "No. BestRx is a cash-pay program — pricing is transparent and starts at $35 your first week. Most patients save money compared to using insurance for these medications.",
  },
  {
    q: "How fast will I get my medication?",
    a: "After provider approval, medication ships within 1–2 business days via discreet packaging. Delivery typically arrives in 2–5 business days depending on your location.",
  },
  {
    q: "What medications are available?",
    a: "Weight Loss (Tirzepatide, Semaglutide, MIC+B12, L-Carnitine), Men's Health (Tadalafil, Sildenafil, PT-141, Enclomiphene), and Women's Health (Hair Restoration, Hormone Optimization, Skin Health, Wellness).",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. There is no long-term contract. Pause or cancel from your patient portal at any time — no fees, no questions.",
  },
  {
    q: "Are the providers licensed?",
    a: "Every provider on the BestRx network is licensed in your state. We never use unlicensed practitioners and never ship medication without a valid provider order.",
  },
  {
    q: "Is my information private?",
    a: "Yes. All medical information is stored using HIPAA-conscious practices, encrypted in transit, and never sold or shared with marketing third parties.",
  },
  {
    q: "What if a medication doesn't work for me?",
    a: "Your provider can adjust dose, switch medications, or pause treatment based on your response. We track outcomes and recommend changes if results aren't where they should be.",
  },
  {
    q: "How do I speak to a real person?",
    a: "Call (214) 815-5966 to reach our care team. We aim to call you back within 30 minutes of any assessment submission.",
  },
];

export default function FAQPage() {
  // Start all FAQs collapsed so the page feels clean and one-at-a-time
  // (avoids the "click item 1 → two open at once" footgun).
  const [openIdx, setOpenIdx] = useState(-1);

  return (
    <main className="bg-white text-black min-h-screen" data-testid="faq-page">
      <SEO
        title="Frequently Asked Questions"
        path="/faq"
        description="Answers to the most common questions about BestRx — how it works, what's available, pricing, shipping, providers, privacy and cancellation."
        schema={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />
      <Nav />

      <section className="border-b border-black/5 bg-[#FAFAFC]">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-6 lg:px-10 py-16 lg:py-20">
          <p className="text-[11px] tracking-[0.22em] font-extrabold uppercase" style={{ color: RED }}>Help Center</p>
          <h1 className="mt-3 font-heavy tracking-[-0.02em] leading-[0.95] text-black text-5xl sm:text-6xl">
            Frequently Asked Questions.
          </h1>
          <p className="mt-5 text-[15px] text-black/65 max-w-[560px]">
            Direct answers to the questions patients ask most. Can't find what you need? Call us at <a className="text-black font-extrabold underline underline-offset-2" href="tel:+12148155966">(214) 815-5966</a>.
          </p>
        </div>
      </section>

      <section className="py-14 lg:py-16">
        <div className="max-w-[860px] mx-auto px-5 sm:px-6 lg:px-10 space-y-2">
          {FAQS.map((f, i) => {
            const isOpen = openIdx === i;
            return (
              <div key={i} className="rounded-xl border bg-white"
                style={{ borderColor: "rgba(0,0,0,0.08)" }}
                data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpenIdx(isOpen ? -1 : i)}
                  data-testid={`faq-toggle-${i}`}
                  className="w-full flex items-center justify-between gap-4 text-left px-5 sm:px-6 py-4 sm:py-5"
                  aria-expanded={isOpen}
                >
                  <span className="font-heavy text-[15.5px] sm:text-[17px] text-black tracking-tight leading-tight">
                    {f.q}
                  </span>
                  <ChevronDown
                    size={18}
                    className={`flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    style={{ color: RED }}
                  />
                </button>
                {isOpen && (
                  <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-[14px] text-black/70 leading-relaxed">
                    {f.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-[#FAFAFC] border-t border-black/5">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-6 lg:px-10 py-14 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Link to="/contact"
            data-testid="faq-cta-contact"
            className="flex items-center justify-between gap-3 px-6 py-5 rounded-xl border bg-white hover:-translate-y-0.5 transition-transform"
            style={{ borderColor: "rgba(0,0,0,0.08)" }}>
            <div>
              <p className="text-[10.5px] tracking-[0.22em] font-extrabold uppercase" style={{ color: RED }}>Still have questions?</p>
              <p className="font-heavy text-[18px] text-black mt-1">Contact Our Care Team</p>
            </div>
            <MessageCircle size={20} className="text-black/60" />
          </Link>
          <a href="tel:+12148155966"
            data-testid="faq-cta-call"
            className="flex items-center justify-between gap-3 px-6 py-5 rounded-xl bg-black text-white hover:-translate-y-0.5 transition-transform">
            <div>
              <p className="text-[10.5px] tracking-[0.22em] font-extrabold uppercase" style={{ color: RED }}>Talk to a human</p>
              <p className="font-heavy text-[18px] mt-1">(214) 815-5966</p>
            </div>
            <Phone size={20} />
          </a>
        </div>
      </section>

      <Footer />
    </main>
  );
}
